import React from "react";
import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import { MentionedAsset, mentionToken } from "../hooks/useAtMention";
import { renderMentionChipInner } from "../panelComponents/AtMentionPicker";

export type SerializedMentionNode = Spread<
  {
    entity: MentionedAsset;
  },
  SerializedLexicalNode
>;

// The inline pill rendered inside the editor for a tagged asset/group. Shows only the entity's own name (no "Group /" prefix) to keep the pill compact; the serialized token still carries the full "Group / Asset" label.
const MentionPill: React.FC<{ entity: MentionedAsset }> = ({ entity }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-eai-cta-primary/30 px-1.5 py-px text-sm font-medium text-eai-cta-primary align-baseline max-w-[220px]">
    {renderMentionChipInner(entity, undefined, {
      size: 16,
      groupNameClass: "text-eai-cta-primary/60 shrink-0",
      nameClass: "truncate",
    })}
  </span>
);

/** Atomic inline mention. The linchpin is getTextContent(): it returns the raw `@{label}​` token, so the editor root's text content IS the canonical message string — persistence, MENTION_REGEX transcript rendering and resolveMentionsFromText all keep working unchanged. */
export class MentionNode extends DecoratorNode<JSX.Element> {
  __entity: MentionedAsset;

  static getType(): string {
    return "mention";
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__entity, node.__key);
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    return $createMentionNode(serializedNode.entity);
  }

  constructor(entity: MentionedAsset, key?: NodeKey) {
    super(key);
    this.__entity = entity;
  }

  exportJSON(): SerializedMentionNode {
    return {
      type: "mention",
      version: 1,
      entity: this.__entity,
    };
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement("span");
    span.className = "inline-block align-baseline";
    span.setAttribute("data-mention-id", this.__entity.id);
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.textContent = this.getTextContent();
    return { element };
  }

  getTextContent(): string {
    return mentionToken(this.__entity);
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  getEntity(): MentionedAsset {
    return this.__entity;
  }

  decorate(): JSX.Element {
    return <MentionPill entity={this.__entity} />;
  }
}

export function $createMentionNode(entity: MentionedAsset): MentionNode {
  return $applyNodeReplacement(new MentionNode(entity));
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
